// May this studio use the app today, and why.
//
// One pure function over three inputs — the account document, the grant log,
// and a date — and it is the only place in the codebase that answers the
// question. The client reads it to draw the Membership tab and the trial
// banner; the admin CLI reads it to print what an account looks like before and
// after a change; `accessWindow` below turns it into the window firestore.rules
// enforce.
//
// ── Why this is a fold and not a stored flag ────────────────────────────────
//
// A boolean on the account — `active: true` — written by the Stripe webhook
// would be a stored copy of a derivable value, as a stored balance would be in
// domain/ledger.js, and the day the two disagreed a paying studio would be
// locked out or a lapsed one not. Access is a function of dates, and a trial
// that ends on Tuesday ends on Tuesday whether or not a worker ran.
//
// So: Stripe's webhook writes down *facts* — what it charged, what it paid
// through, whether the card failed. By-hand decisions are appended to a grant
// log. This function folds all of it into a verdict, every time, from scratch.
//
// ── Why our record and not Stripe's ─────────────────────────────────────────
//
// Access has to work when there is no Stripe subscription at all. A comped
// account never has one. An account inside its trial has not been asked for a
// card. A studio awarded months by hand might have neither. Stripe is one of
// several ways an account comes to be entitled.

import { todayISO } from './clock.js';
import { addDays } from './recurrence.js';
import {
  PLANS, STATES, ENTITLED_STATES, GRANT_KINDS,
  TRIAL_DAYS, PAST_DUE_GRACE_DAYS, RENEWAL_GRACE_DAYS,
  addMonths, priceMinorFor, BILLING_CURRENCY,
} from './billingPlans.js';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const isDate = (v) => typeof v === 'string' && DATE_ONLY.test(v);

// The later of two date-only strings, either of which may be absent. Compared
// as strings, which is exact for YYYY-MM-DD and is how every other date
// comparison in this codebase is done.
const later = (a, b) => {
  if (!isDate(a)) return isDate(b) ? b : null;
  if (!isDate(b)) return a;
  return a > b ? a : b;
};

// Stripe subscription statuses that mean "this studio has paid and is current".
// `trialing` is here because a Stripe-side trial is a real entitlement — it
// arises when an awarded month pushes the next charge out, which is exactly how
// domain-level awards reach a live subscription.
//
// Exported for the admin CLI, which has to know whether a subscription has a
// next charge worth moving before it awards a month; a second definition of
// which statuses count as paid could drift and charge a studio for a month it
// was given.
export const PAYING = new Set(['active', 'trialing']);

// Stripe statuses that mean "there is already a live subscription here".
//
// **Wider than `PAYING`**, and derived from it. `PAYING` answers "may they use
// the app"; this answers "would starting a checkout give them a *second*
// subscription". A `past_due` or `unpaid` subscription is still a subscription
// — Stripe is still retrying the card — and a second one would bill the studio
// twice for the same month. What they need is the portal.
export const LIVE_SUBSCRIPTION = new Set([...PAYING, 'past_due', 'unpaid']);

// Does this studio have a subscription Stripe is still running? The one answer
// to "checkout or the billing portal", asked by the server before it will open
// a checkout (functions/billing/callables.js `startCheckout`) and by the
// Membership tab before it offers one, so the button never leads to a refusal.
//
// Not "has a Stripe customer". A studio that subscribed and then cancelled keeps
// its customer, and Stripe's billing portal cannot start a new subscription, so
// that studio goes to checkout, which reuses the customer.
export function hasLiveSubscription(account) {
  return Boolean(account?.stripeSubscriptionId) && LIVE_SUBSCRIPTION.has(account?.stripeStatus);
}

// ─── The grant log ──────────────────────────────────────────────────────────

// How far awarded months reach, and whether a comp is open.
//
// Folded in date order rather than in write order, so two grants entered out
// of sequence resolve the way the dates say. Awards accumulate from the *later*
// of the grant's own date and where the awards have already reached, so three
// months awarded today and two more awarded next week are five months of access
// rather than two overlapping stretches.
export function foldGrants(grants = [], today = todayISO()) {
  const rows = (Array.isArray(grants) ? grants : [])
    .filter((g) => g && isDate(g.grantedOn))
    // As of today, like domain/ledger.js `balanceAsOf`. A grant dated in the
    // future has not taken effect: a comp written down as ending in June must
    // not close it in January, and an award dated next month must not let
    // anybody in this month. Filtered here rather than at each case.
    .filter((g) => g.grantedOn <= today)
    .slice()
    .sort((a, b) => (a.grantedOn === b.grantedOn
      // Same day: fall back to the id so the fold is deterministic rather than
      // dependent on the order Firestore hands back the documents.
      ? String(a.id || '').localeCompare(String(b.id || ''))
      : (a.grantedOn < b.grantedOn ? -1 : 1)));

  let awardedUntil = null;
  let compFrom = null;    // non-null while a comp is open
  let compEndedOn = null; // when the last comp was closed
  let heldPlan = null;
  let heldPriceMinor = null;
  let monthsAwarded = 0;

  for (const g of rows) {
    switch (g.kind) {
      case GRANT_KINDS.award: {
        const months = Math.trunc(Number(g.months));
        if (!Number.isFinite(months) || months <= 0) break;
        monthsAwarded += months;
        // From wherever the awards have already reached, or from the day the
        // award was made if they have not reached that far. An award made
        // while an earlier one still runs extends it; one made afterwards
        // starts from itself rather than back-dating into a gap the studio
        // has already been locked out of.
        const from = later(awardedUntil, g.grantedOn);
        awardedUntil = addMonths(from, months);
        break;
      }
      case GRANT_KINDS.comp_start:
        compFrom = g.grantedOn;
        compEndedOn = null;
        break;
      case GRANT_KINDS.comp_end:
        compFrom = null;
        compEndedOn = g.grantedOn;
        break;
      case GRANT_KINDS.grandfather:
        heldPlan = g.plan || PLANS.grandfathered;
        heldPriceMinor = Number.isInteger(g.priceMinor) ? g.priceMinor : null;
        break;
      default:
        // An unknown kind is ignored rather than thrown on. This log is
        // append-only and permanent, so a kind an older client does not know
        // must not stop it computing an entitlement.
        break;
    }
  }

  return {
    // Null unless a comp is open *and* has started by today. A comp dated in
    // the future is a decision already recorded that has not taken effect.
    compOpen: isDate(compFrom) && compFrom <= today,
    compFrom,
    compEndedOn,
    awardedUntil,
    monthsAwarded,
    heldPlan,
    // What a stored `grandfather` grant held the studio at (#722). Folded so
    // the record stays readable; nothing prices from it (billingPlans.js
    // `priceMinorFor`).
    heldPriceMinor,
  };
}

// ─── The verdict ────────────────────────────────────────────────────────────

// Everything the app needs to know about one studio's access.
//
//   account  users/{uid}/billing/account, or null for a studio that has never
//            had one written — a real state, not an error: every account
//            between signing up and the callable that stamps it. Treated as
//            "no trial yet", not as "lapsed", so nobody is shown a paywall
//            because a write was slow.
//   grants   users/{uid}/billingGrants, in any order
//   today    the studio's own calendar day
//
// Returns:
//   {
//     state, canUse, accessUntil, daysLeft,
//     plan, priceMinor, currency,
//     reason, trialEndsOn, paidThroughOn, awardedUntil, compOpen,
//   }
//
// `reason` is a stable string, not a sentence: it is what the run logs are
// grepped by and what the Membership tab switches its copy on.
export function entitlementAsOf({ account = null, grants = [], today = todayISO() } = {}) {
  const acc = account || {};
  const folded = foldGrants(grants, today);

  // A grandfathered plan held by hand beats what the account document says —
  // the grant log is the record of the decision and the account a cache of
  // it. No writer sets either (#722); they are read so a stored account that
  // has one keeps its plan's name. Neither the grant's price nor the account's
  // moves what it costs (`priceMinorFor`).
  //
  // An open comp is the plan while it lasts. It arrives as a `comp_start` grant
  // rather than as a plan on the account, so without this a comped studio would
  // be quoted a monthly price it is never charged.
  const plan = folded.compOpen
    ? PLANS.comp
    : (folded.heldPlan || acc.plan || PLANS.standard);
  const priceMinor = priceMinorFor({ plan });

  // Required, not derived. `newAccount` below writes `signedUpOn` and
  // `trialEndsOn` together and is the only shape `ensureAccount` hands to
  // `applyToAccount`, and both fields are on IMMUTABLE_FIELDS
  // (functions/billing/account.js), so an account with a signup date and no
  // trial end is a shape no writer produces. If one existed it would fold to
  // `lapsed` — fail-closed, unlike the rest of this file.
  const trialEndsOn = isDate(acc.trialEndsOn) ? acc.trialEndsOn : null;

  const paidThroughOn = isDate(acc.paidThroughOn) ? acc.paidThroughOn : null;
  const stripeStatus = acc.stripeStatus || null;

  // How long each source of access reaches. Null means "this one does not
  // apply", which is not the same as "it expired yesterday".
  const paidUntil = PAYING.has(stripeStatus) ? paidThroughOn : null;
  const graceUntil = stripeStatus === 'past_due' && paidThroughOn
    ? addDays(paidThroughOn, PAST_DUE_GRACE_DAYS)
    : null;
  // The renewal margin. In billing/webhook.js `paidThroughOn` moves only on
  // evidence of *payment*, so a subscription Stripe still calls `active`
  // spends the gap between one period ending and its renewal invoice being
  // paid with `paidThroughOn` in the past. Without this the fold would answer
  // `lapsed` for a healthy paying studio once a month, and longer whenever a
  // webhook is delayed.
  //
  // Only for `active`. A `trialing` subscription's `paidThroughOn` is the
  // granted trial end and there is no invoice in flight behind it, and every
  // other status has its own answer above or none at all.
  const renewalUntil = stripeStatus === 'active' && paidThroughOn
    ? addDays(paidThroughOn, RENEWAL_GRACE_DAYS)
    : null;
  const trialUntil = trialEndsOn;
  const awardedUntil = folded.awardedUntil;

  // The verdict, in the order the states are worth reporting. A studio often
  // satisfies more than one — comped inside a trial, awarded months on top of a
  // live subscription — and what is reported is the one *paying for the
  // access*, because that is the sentence the Membership tab has to write.
  let state;
  let reason;
  if (folded.compOpen) {
    state = STATES.comped;
    reason = 'comped_indefinitely';
  } else if (paidUntil && today <= paidUntil) {
    state = STATES.active;
    reason = 'subscription_paid';
  } else if (awardedUntil && today <= awardedUntil) {
    state = STATES.granted;
    reason = 'awarded_months';
  } else if (trialUntil && today <= trialUntil) {
    state = STATES.trial;
    reason = 'in_trial';
  } else if (renewalUntil && today <= renewalUntil) {
    // `grace` rather than `active`: they are in on a promise, not a payment,
    // and the Membership tab switches on the distinction. The reason says
    // which kind of grace: nothing has failed, the renewal invoice is pending.
    state = STATES.grace;
    reason = 'renewal_pending';
  } else if (graceUntil && today <= graceUntil) {
    state = STATES.grace;
    reason = 'payment_failed_retrying';
  } else if (!isDate(acc.signedUpOn) && !trialUntil) {
    // Nothing has been written down about this studio's billing at all: an
    // absence of information, reported as its own state so a caller cannot
    // mistake it for a verdict. `unknown` is entitled (see ENTITLED_STATES),
    // because a gate over money fails open. Reached in the seconds between
    // signing up and the trigger that stamps the account, and in a deployment
    // without the billing functions.
    state = STATES.unknown;
    reason = 'no_account';
  } else {
    state = STATES.lapsed;
    // Which door they came out of, because "your trial ended" and "your card
    // stopped working" are different screens and different emails.
    if (stripeStatus === 'canceled') reason = 'subscription_cancelled';
    else if (graceUntil) reason = 'payment_failed_grace_over';
    // An `active` subscription whose paid period ended more than the renewal
    // margin ago: a renewal that never arrived — a webhook never received, or
    // a subscription Stripe thinks is fine that has not billed. The reason to
    // grep for when a paying studio reports being locked out.
    else if (renewalUntil) reason = 'subscription_unpaid';
    else if (folded.compEndedOn) reason = 'comp_ended';
    else if (awardedUntil) reason = 'awarded_months_used_up';
    else reason = 'trial_ended';
  }

  // The furthest any source reaches — not the reason's own end date, and NOT a
  // promise that it is in the future. On a lapsed account this is the day
  // access ran out (the Membership tab says when), so a caller must check
  // `state` before printing it; `daysLeft` is null there.
  //
  // A studio paid to the 30th with two months awarded on top is `active`, but
  // their access does not end on the 30th, so `accessUntil` is the maximum.
  // `renewalUntil` is included for the same reason: an `active` studio's
  // access reaches the renewal margin past the paid-through date, which is
  // when the fold stops letting them in.
  const accessUntil = folded.compOpen
    // A comp has no end until one is written. `null` here means "indefinitely",
    // and every caller has to handle that anyway for the same reason.
    ? null
    : [paidUntil, awardedUntil, trialUntil, graceUntil, renewalUntil].reduce(later, null);

  const canUse = ENTITLED_STATES.includes(state);

  return {
    state,
    canUse,
    accessUntil,
    // Whole days from today to the end of access, inclusive of today. Null when
    // access is indefinite (a comp) or already over.
    daysLeft: accessUntil && today <= accessUntil ? daysBetween(today, accessUntil) : null,
    plan,
    priceMinor,
    currency: acc.currency || BILLING_CURRENCY,
    reason,
    trialEndsOn,
    paidThroughOn,
    awardedUntil,
    monthsAwarded: folded.monthsAwarded,
    compOpen: folded.compOpen,
  };
}

// ─── The access window ──────────────────────────────────────────────────────
//
// The verdict above is what the client draws and the admin CLI prints. This is
// the one field of it that firestore.rules enforces, shaped so rules can read
// it: a Timestamp the server clock is compared against, or "no end".
//
// Rules cannot fold a grant log, parse a date-only string, or ask what day it
// is in the studio's time zone; they can compare `request.time` with a stored
// Timestamp. So the fold's answer is materialised onto `billing/account` as
// `access` by the one writer (functions/billing/account.js `applyToAccount`),
// re-derived on every grant write and once a day. This is the pure part:
// verdict in, window out. A **date, not a flag**, so it ends on its day
// whether or not anything ran.
//
// ── Why `unknown` is indefinite ─────────────────────────────────────────────
//
// `unknown` is on ENTITLED_STATES because a gate over money fails open, and the
// window says the same: no end. An account document can only be written by a
// server (`billing/*` refuses every client write), so a client cannot arrange
// this state for itself.
//
// ── Why two days of margin ──────────────────────────────────────────────────
//
// The fold is inclusive: `today <= accessUntil`, where `today` is the studio's
// own calendar day. The rules compare an instant. A trial that ends on the 31st
// is good *through* the 31st in the studio's zone, and the last instant of the
// 31st in Pago Pago (UTC−11) is 11:00Z on the 1st; the first instant of the
// 1st in Kiritimati (UTC+14) is 10:00Z on the 31st. Every zone is inside 26
// hours of UTC in both directions, so "the 31st, anywhere" is contained in
// [31st 00:00Z − 14h, 1st 00:00Z + 11h]. Two whole days, added on the UTC
// grid, cover that with room to spare and are easy to say out loud.
//
// So a lapsed studio is refused writes **two calendar days after the date the
// Membership tab shows**, not on it — the direction in which no studio whose
// tab says "ends today" is refused a write because it is already tomorrow in
// Greenwich.
//
// A grant dated in the future is filtered out by `foldGrants`, so the window
// written today does not know about it. The next account write, or the daily
// refresh, picks it up — a day early, thanks to the margin, and never late.
export const ACCESS_MARGIN_DAYS = 2;

export function accessWindow({ account = null, grants = [], today = todayISO() } = {}) {
  const verdict = entitlementAsOf({ account, grants, today });
  const indefinite = verdict.state === STATES.comped || verdict.state === STATES.unknown;
  // May be in the past. That is the point: a lapsed studio's window is the
  // day its access ran out, and the rules read "closed" from it directly.
  const until = indefinite ? null : verdict.accessUntil;
  const untilMs = until
    ? Date.parse(`${addDays(until, ACCESS_MARGIN_DAYS)}T00:00:00Z`)
    : null;
  return { state: verdict.state, until, untilMs, indefinite };
}

// Calendar days between two date-only strings, on the UTC grid.
//
// A local copy of domain/reminders.js's, rather than an import between two
// modules that do not otherwise depend on each other.
function daysBetween(from, to) {
  if (!isDate(from) || !isDate(to)) return null;
  const ms = Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`);
  return Math.round(ms / 86400000);
}

// A studio that has just signed up, as the account document should first be
// written.
//
// Here rather than in the Cloud Function so the client's tests, the function's
// tests and the admin CLI agree on the shape. The caller supplies the day from
// a *server* clock — a trial measured by the signing-up browser's clock could
// be made as long as anybody liked.
//
// Every new account is `standard`, with no cohort and no `introInvoicesUsed`
// (#722); a stored account may still carry both.
export function newAccount({ signedUpOn, currency = BILLING_CURRENCY }) {
  if (!isDate(signedUpOn)) throw new Error('newAccount needs a date-only signedUpOn');
  return {
    signedUpOn,
    trialEndsOn: addDays(signedUpOn, TRIAL_DAYS),
    plan: PLANS.standard,
    priceMinor: null,
    currency,
    stripeCustomerId: null,
    stripeSubscriptionId: null,
    stripeStatus: null,
    paidThroughOn: null,
  };
}
